// TROIA-V3-FRONTEND/src/modules/crm/seller-dashboard/components/SellerMobileHome.tsx
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { SellerDashboardResponse } from '@troia-v3/types';

import { Avatar, AvatarFallback, AvatarImage } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';
import { formatPeriodLabel as formatGamificationPeriodLabel, periodInLabel } from '@/shared/utils/gamificationPeriod';

import { formatPhoneBR, formatPts, formatRelative, type SellerPeriod } from '../lib/sellerFormat';

import { LevelProgress } from './MyLevelCard';

export type SellerMobileTab = 'home' | 'leads' | 'ranking';

interface SellerMobileProps {
  tab: SellerMobileTab;
  data: SellerDashboardResponse;
  firstName: string;
  userId: string;
  unitName?: string;
  /**
   * Troca a janela buscada na API. O celular NÃO recebe o `period` atual: o
   * chip ativo da aba "Meus leads" é estado local (dois chips apontam para o
   * mesmo período, ver `LEADS_CHIPS`), então derivar o destaque do `period`
   * acendia os dois.
   */
  onPeriodChange: (p: SellerPeriod) => void;
  onStart: () => void;
  onTabChange: (t: SellerMobileTab) => void;
}

const MKpi: React.FC<{ label: string; value: React.ReactNode; testId: string }> = ({ label, value, testId }) => (
  <div className="rounded-xl p-3" style={{ backgroundColor: 'var(--theme-card-background)', border: '1px solid var(--theme-card-border)' }} data-testid={testId}>
    <div className="text-[11px] font-semibold" style={{ color: 'var(--theme-muted-text)' }}>{label}</div>
    <div className="text-[22px] font-bold tracking-[-.02em] mt-0.5" style={{ color: 'var(--theme-card-text)', fontVariantNumeric: 'tabular-nums' }}>{value}</div>
  </div>
);

const MSec: React.FC<{ label: string; action?: { label: string; onClick?: () => void; to?: string; testId: string } }> = ({ label, action }) => (
  <div className="text-[11px] font-bold uppercase tracking-[.06em] flex items-center mt-1" style={{ color: 'var(--theme-muted-text)' }}>
    {label}
    {action && (action.to
      ? <Link to={action.to} className="ml-auto normal-case tracking-normal text-xs font-semibold" style={{ color: 'var(--theme-primary)' }} data-testid={action.testId}>{action.label}</Link>
      : <button type="button" onClick={action.onClick} className="ml-auto normal-case tracking-normal text-xs font-semibold" style={{ color: 'var(--theme-primary)' }} data-testid={action.testId}>{action.label}</button>)}
  </div>
);

const MList: React.FC<{ children: React.ReactNode; testId?: string }> = ({ children, testId }) => (
  <div className="rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--theme-card-background)', border: '1px solid var(--theme-card-border)' }} data-testid={testId}>{children}</div>
);

const MRow: React.FC<{ name: React.ReactNode; sub?: string; right: React.ReactNode; me?: boolean; pos?: number; testId: string }> = ({ name, sub, right, me, pos, testId }) => (
  <div className={`flex items-center gap-2.5 px-3 py-2.5 border-t first:border-t-0 ${me ? 'seller-rk-row--me' : ''}`} style={{ borderColor: 'var(--theme-card-border)' }} data-testid={testId}>
    {pos !== undefined && <span className={`seller-rk-pos ${pos === 1 ? 'seller-rk-pos--gold' : pos === 2 ? 'seller-rk-pos--silver' : pos === 3 ? 'seller-rk-pos--bronze' : ''}`}>{pos}</span>}
    <div className="min-w-0">
      <div className="font-semibold text-sm tracking-[-.01em] truncate" style={{ color: 'var(--theme-card-text)' }}>{name}</div>
      {sub && <div className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>{sub}</div>}
    </div>
    <div className="ml-auto text-right text-xs whitespace-nowrap" style={{ color: 'var(--theme-muted-text)' }}>{right}</div>
  </div>
);

const you = <span className="ml-1 text-[10px] font-bold rounded-full px-[7px] py-px" style={{ color: 'var(--theme-primary)', backgroundColor: 'var(--theme-primary-background)' }}>você</span>;

/** `a`/`b` no mesmo dia local (evita off-by-one perto da meia-noite via UTC). */
const isSameLocalDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

/**
 * Chips da aba "Meus leads" (mockup 08 `leads()`): Hoje/Semana/Mês/Todos.
 * "Todos" não tem período próprio no contrato desta parte — `SellerPeriod`
 * só tem `today | week | month` — então busca o mesmo `month` da "Mês".
 *
 * Por isso o chip ATIVO é estado próprio, não derivado do `period`: com
 * `aria-pressed={period === chipPeriod}` os dois chips que apontam pra
 * `month` acendiam JUNTOS, e o leitor de tela anunciava dois filtros ativos
 * ao mesmo tempo. Agora só o chip clicado fica marcado.
 */
type LeadsChipId = 'today' | 'week' | 'month' | 'all';

const LEADS_CHIPS: ReadonlyArray<{ id: LeadsChipId; label: string; period: SellerPeriod }> = [
  { id: 'today', label: 'Hoje', period: 'today' },
  { id: 'week', label: 'Semana', period: 'week' },
  { id: 'month', label: 'Mês', period: 'month' },
  { id: 'all', label: 'Todos', period: 'month' },
];

/**
 * Anatomia própria do celular (mockup 08) — NÃO é o desktop espremido. 3 abas
 * (Início · Meus leads · Ranking) selecionadas pela linha `mobile` do
 * `PageToolbar` na página. `hidden` decide a variante C (ranking oculto):
 * `!ranking` (gamificação desligada/sem recorte) OU `ranking.visibility ===
 * 'only_me'` — mesmo critério do `StoreRankingCard` (desktop).
 */
export const SellerMobileHome: React.FC<SellerMobileProps> = ({ tab, data, firstName, userId, unitName, onPeriodChange, onStart, onTabChange }) => {
  const { kpis, ranking } = data;
  const hidden = !ranking || ranking.visibility === 'only_me';
  const [leadsChip, setLeadsChip] = useState<LeadsChipId>('today');
  /**
   * Mockup 08: a aba "Meus leads" abre em "Hoje". Como o `period` é do
   * dashboard inteiro (o desktop usa "Mês" por padrão), entrar na aba
   * reposiciona os dois — chip e requisição — no mesmo lugar.
   * `onPeriodChange` é um `useCallback` sem dependências na página
   * (`selectPreset`): identidade estável, então o efeito só roda de fato na
   * troca de aba. O celular não oferece "Personalizado"; se o intervalo foi
   * escolhido no desktop, entrar na aba volta para "Hoje".
   */
  useEffect(() => {
    if (tab !== 'leads') return;
    setLeadsChip('today');
    onPeriodChange('today');
  }, [tab, onPeriodChange]);
  const leadRow = (lead: SellerDashboardResponse['recentLeads'][number]) => (
    <MRow key={lead.id} testId={`seller-mobile-lead-${lead.id}`}
      name={lead.contact?.name ?? 'Lead'}
      sub={`${formatPhoneBR(lead.contact?.identifiers?.whatsapp?.[0] ?? lead.contact?.identifiers?.phone?.[0] ?? '')} · ${lead.step?.name ?? '—'}${lead.origin === 'qrcode' ? ' · QR' : ''}`}
      right={formatRelative(String(lead.createdAt))} />
  );

  if (tab === 'leads') {
    const now = new Date();
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    const todayLeads = data.recentLeads.filter((lead) => isSameLocalDay(new Date(lead.createdAt), now));
    const yesterdayLeads = data.recentLeads.filter((lead) => isSameLocalDay(new Date(lead.createdAt), yesterday));
    const olderLeads = data.recentLeads.filter((lead) => !isSameLocalDay(new Date(lead.createdAt), now) && !isSameLocalDay(new Date(lead.createdAt), yesterday));

    return (
      <div className="flex flex-col gap-2.5" data-testid="seller-mobile-leads">
        <div className="flex gap-1.5 overflow-x-auto">
          {LEADS_CHIPS.map(({ id, label, period: chipPeriod }) => (
            <button key={id} type="button" className="seller-chip" aria-pressed={leadsChip === id}
              onClick={() => { setLeadsChip(id); onPeriodChange(chipPeriod); }} data-testid={`seller-mobile-chip-${id}`}>{label}</button>
          ))}
        </div>
        {data.recentLeads.length === 0 ? (
          <MList><div className="px-4 py-8 text-center text-[13px]" style={{ color: 'var(--theme-muted-text)' }}>Nenhum lead seu neste período.</div></MList>
        ) : (
          <>
            {todayLeads.length > 0 && <MList testId="seller-mobile-leads-today">{todayLeads.map(leadRow)}</MList>}
            {yesterdayLeads.length > 0 && (
              <>
                <MSec label="Ontem" />
                <MList testId="seller-mobile-leads-yesterday">{yesterdayLeads.map(leadRow)}</MList>
              </>
            )}
            {olderLeads.length > 0 && (
              <>
                <MSec label="Anteriores" />
                <MList testId="seller-mobile-leads-older">{olderLeads.map(leadRow)}</MList>
              </>
            )}
          </>
        )}
      </div>
    );
  }

  if (tab === 'ranking') {
    return (
      <div className="flex flex-col gap-2.5 pb-16" data-testid="seller-mobile-ranking">
        <div className="flex items-center gap-2 min-w-0">
          <span className="inline-flex items-center gap-1.5 text-[15px] font-semibold tracking-[-.01em] min-w-0 truncate" style={{ color: 'var(--theme-card-text)' }}>
            <span className="truncate">{unitName ? `Loja ${unitName}` : 'Empresa'}</span>
            <Icon name="chevron-down" size={14} style={{ color: 'var(--theme-muted-text)' }} />
          </span>
          {/* Rótulo do período REAL da gamificação (`ranking.period`): antes era
              `formatPeriodLabel('month', …)` com o mês fixo, então uma empresa
              configurada em semana/trimestre via "Agosto" com pontos de outra
              janela. */}
          <span className="ml-auto flex-none inline-flex items-center h-[26px] px-2.5 rounded-full text-xs font-semibold whitespace-nowrap"
            style={{ backgroundColor: 'var(--theme-surface-sunken)', color: 'var(--theme-muted-text)' }} data-testid="seller-mobile-ranking-period">
            {ranking ? formatGamificationPeriodLabel(ranking.period, ranking.periodKey) : ''}
          </span>
        </div>
        {hidden ? (
          <MList><div className="px-4 py-10 text-center text-[13px]" style={{ color: 'var(--theme-muted-text)' }} data-testid="seller-mobile-ranking-hidden">A empresa optou por mostrar só os seus números.<br />Seus pontos e nível continuam contando.</div></MList>
        ) : (
          <>
            <MList>
              <div className="grid grid-cols-[1fr_1.2fr_1fr] gap-2 items-end px-2.5 pt-[18px] pb-2.5">
                {[ranking.entries[1], ranking.entries[0], ranking.entries[2]].map((e, i) => e && (
                  <div key={e.id} className="relative rounded-xl text-center px-2 pt-5 pb-3" style={{ border: '1px solid var(--theme-card-border)', background: i === 1 ? 'linear-gradient(180deg, var(--theme-medal-gold-subtle), var(--theme-card-background) 70%)' : 'var(--theme-card-background)' }} data-testid={`seller-mobile-podium-${e.position}`}>
                    <span className={`absolute -top-3.5 left-1/2 -translate-x-1/2 w-[30px] h-[30px] rounded-full font-bold text-[13px] flex items-center justify-center seller-rk-pos seller-rk-pos--${e.position === 1 ? 'gold' : e.position === 2 ? 'silver' : 'bronze'}`}>{e.position}</span>
                    {/* Avatar shared (foto ou fallback slate do DS) — sem cor própria aqui */}
                    <Avatar className="mx-auto mb-2 rounded-xl font-bold" style={{ width: i === 1 ? 54 : 44, height: i === 1 ? 54 : 44 }}>
                      {e.avatar && <AvatarImage src={e.avatar} alt={e.name} className="rounded-xl" />}
                      <AvatarFallback className="rounded-xl">{e.name.split(' ').slice(0, 2).map((p) => p[0]).join('')}</AvatarFallback>
                    </Avatar>
                    <div className="font-bold truncate" style={{ fontSize: e.position === 1 ? 13 : 12, color: 'var(--theme-card-text)' }}>{e.name.split(' ')[0]}</div>
                    <div className="font-bold mt-1.5" style={{ fontSize: i === 1 ? 20 : 17, color: 'var(--theme-card-text)', fontVariantNumeric: 'tabular-nums' }}>{formatPts(e.points)}</div>
                  </div>
                ))}
              </div>
            </MList>
            <MList>{ranking.entries.slice(3).map((e) => <MRow key={e.id} pos={e.position} testId={`seller-mobile-rank-${e.id}`} name={<>{e.name}{e.id === userId && you}</>} me={e.id === userId} right={<span className="font-bold text-sm" style={{ color: 'var(--theme-card-text)' }}>{formatPts(e.points)} pts</span>} />)}</MList>
          </>
        )}
      </div>
    );
  }

  const todayHomeLeads = data.recentLeads.filter((lead) => isSameLocalDay(new Date(lead.createdAt), new Date()));

  return (
    <div className="flex flex-col gap-2.5" data-testid="seller-mobile-home">
      <div className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>Olá, vendedor</div>
      <div className="font-bold text-xl tracking-[-.02em] -mt-2" style={{ color: 'var(--theme-card-text)' }}>{firstName} 👋</div>
      <div className="rounded-[14px] p-3.5" style={{ border: '1px solid var(--theme-card-border)', background: 'linear-gradient(180deg, var(--theme-card-background), var(--theme-surface-highlight))' }}>
        <div className="font-bold text-[15px] tracking-[-.02em]" style={{ color: 'var(--theme-text)' }}>Pronto para atender?</div>
        <div className="text-xs mt-0.5 mb-2.5" style={{ color: 'var(--theme-muted-text)' }}>Gere o QR Code e o cliente se cadastra no próprio celular.</div>
        <button type="button" onClick={onStart} className="h-11 w-full rounded-[10px] font-semibold text-sm flex items-center justify-center gap-2" style={{ backgroundColor: 'var(--theme-button-primary)', color: 'var(--theme-button-primary-text)' }} data-testid="seller-mobile-start">
          <Icon name="qr-code" size={18} />Iniciar atendimento
        </button>
      </div>
      <div className="grid grid-cols-2 gap-2.5">
        <MKpi label="Leads hoje" value={formatPts(kpis.leadsToday)} testId="seller-mobile-kpi-today" />
        <MKpi label="Leads no mês" value={formatPts(kpis.leadsMonth)} testId="seller-mobile-kpi-month" />
        <MKpi label={`Pontos ${periodInLabel(data.gamificationPeriod)}`} value={<span style={{ color: 'var(--theme-primary)' }}>{formatPts(kpis.periodPoints)}</span>} testId="seller-mobile-kpi-points" />
        <MKpi label={hidden ? 'Nível' : 'Posição'} value={hidden ? kpis.level.name : <>{kpis.position ? `#${kpis.position}` : '—'} {ranking?.me?.trend === 'up' && <span className="text-xs font-semibold" style={{ color: 'var(--theme-success-fg)' }}>↑1</span>}</>} testId="seller-mobile-kpi-position" />
      </div>
      <MSec label="Meus leads de hoje" action={{ label: 'Ver todos', onClick: () => onTabChange('leads'), testId: 'seller-mobile-leads-all' }} />
      {/* G5: o título diz "de hoje", então a lista é de HOJE. `recentLeads` vem
          na janela do período selecionado (mês, por padrão) — o `slice(0,3)`
          anterior mostrava os 3 mais recentes do MÊS embaixo desse título. */}
      <MList testId="seller-mobile-home-leads">
        {todayHomeLeads.slice(0, 3).map(leadRow)}
        {todayHomeLeads.length === 0 && <div className="px-4 py-6 text-center text-[13px]" style={{ color: 'var(--theme-muted-text)' }}>Nenhum lead ainda hoje.</div>}
      </MList>
      {!hidden && ranking && (
        <>
          <MSec label="Ranking da loja" action={{ label: 'Ver completo', onClick: () => onTabChange('ranking'), testId: 'seller-mobile-ranking-full' }} />
          <MList>{ranking.entries.slice(0, 4).map((e) => <MRow key={e.id} pos={e.position} testId={`seller-mobile-rank-${e.id}`} name={<>{e.name}{e.id === userId && you}</>} me={e.id === userId} right={<span className="font-bold text-sm" style={{ color: 'var(--theme-card-text)' }}>{formatPts(e.points)} pts</span>} />)}</MList>
        </>
      )}
      <div className="rounded-xl p-3" style={{ backgroundColor: 'var(--theme-card-background)', border: '1px solid var(--theme-card-border)' }}>
        <LevelProgress totalPoints={kpis.totalPoints} level={kpis.level} {...(kpis.nextLevel ? { nextLevel: kpis.nextLevel } : {})} />
      </div>
    </div>
  );
};
SellerMobileHome.displayName = 'SellerMobileHome';
